import React, { useState, useEffect } from 'react';
import Timer from './components/Timer';
import Footer from './components/Footer';
import Header from './components/Header';
import TaskManager from './components/TaskManager';
import Statistics from './components/Statistics';
import SessionHistory from './components/SessionHistory';
import Goals from './components/Goals';
import BreakReminder from './components/BreakReminder';
import { Container, Row, Col } from 'react-bootstrap';
import { IconButton, Box, Button, Drawer, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import { Brightness4, Brightness7, Menu, Close } from '@mui/icons-material';
import './App.css';

const App = () => {
  // State to track theme mode
  const [darkMode, setDarkMode] = useState(() => {
    // Load initial theme from localStorage
    return localStorage.getItem('darkMode') === 'true';
  });

  const [currentView, setCurrentView] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Update `body` class and save theme to localStorage on mode change
  useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  // Toggle theme handler
  const handleToggleDarkMode = () => setDarkMode((prevMode) => !prevMode);

  const navigationItems = [
    { label: 'Dashboard', value: 'dashboard' },
    { label: 'Statistics', value: 'statistics' },
    { label: 'Session History', value: 'history' },
    { label: 'Goals', value: 'goals' },
    { label: 'Break Reminders', value: 'breaks' },
  ];

  const renderView = () => {
    switch (currentView) {
      case 'statistics':
        return <Statistics />;
      case 'history':
        return <SessionHistory />;
      case 'goals':
        return <Goals />;
      case 'breaks':
        return <BreakReminder />;
      default:
        return (
          <Container fluid className="mt-4 px-4">
            <Row className="justify-content-center align-items-start gx-4">
              {/* Timer Section */}
              <Col xs={12} md={6} lg={5} className="mb-4">
                <div className="component-wrapper shadow p-3 rounded">
                  <Timer />
                </div>
              </Col>

              {/* Task Manager Section */}
              <Col xs={12} md={6} lg={5} className="mb-4">
                <div className="component-wrapper shadow p-3 rounded">
                  <TaskManager />
                </div>
              </Col>
            </Row>
          </Container>
        );
    }
  };

  return (
    <>
      <Header />
      <div className={`app-container ${darkMode ? 'dark' : 'light'}`}>
        {/* Dark Mode Toggle Button */}
        <IconButton
          onClick={handleToggleDarkMode}
          sx={{
            position: 'fixed',
            top: 10,
            right: 60,
            zIndex: 1000,
            color: darkMode ? '#FFD700' : '#333',
            '&:hover': {
              color: darkMode ? '#FFC107' : '#000',
            },
          }}
        >
          {darkMode ? <Brightness7 fontSize="large" /> : <Brightness4 fontSize="large" />}
        </IconButton>

        {/* Mobile Menu Button */}
        <IconButton
          onClick={() => setMobileMenuOpen(true)}
          sx={{
            position: 'fixed',
            top: 10,
            right: 10,
            zIndex: 1000,
            color: darkMode ? '#FFD700' : '#333',
            display: { xs: 'block', md: 'none' },
          }}
        >
          <Menu fontSize="large" />
        </IconButton>

        {/* Navigation Drawer */}
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
        >
          <Box sx={{ width: 250, pt: 2 }}>
            <IconButton
              onClick={() => setMobileMenuOpen(false)}
              sx={{ float: 'right', mr: 1 }}
            >
              <Close />
            </IconButton>
            <List>
              {navigationItems.map((item) => (
                <ListItem key={item.value} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      setCurrentView(item.value);
                      setMobileMenuOpen(false);
                    }}
                    selected={currentView === item.value}
                  >
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Drawer>

        {/* Desktop Navigation */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            justifyContent: 'center',
            gap: 2,
            p: 2,
            backgroundColor: darkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.5)',
            borderBottom: '2px solid #ddd',
          }}
        >
          {navigationItems.map((item) => (
            <Button
              key={item.value}
              onClick={() => setCurrentView(item.value)}
              variant={currentView === item.value ? 'contained' : 'text'}
              sx={{
                background: currentView === item.value ? 'linear-gradient(45deg, #3F51B5, #2196F3)' : 'transparent',
                color: currentView === item.value ? '#fff' : 'inherit',
              }}
            >
              {item.label}
            </Button>
          ))}
        </Box>

        {/* Main Content */}
        {renderView()}
      </div>
      <Footer />
    </>
  );
};

export default App;
